import { CUSTOM_ELEMENTS_SCHEMA, ModuleWithProviders, NgModule } from '@angular/core';
import { MedcareCoreUiComponent } from './medcare-core-ui.component';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { AccordionAnchorDirective, AccordionDirective, AccordionLinkDirective, AdminLayoutComponent, AuthLayoutComponent, HeaderComponent, MenuComponent, profileDialog, settingsDialog, SidebarComponent, unitDialog } from './core';
import { CommonModule, LocationStrategy, HashLocationStrategy } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { RichTextEditorModule } from '@syncfusion/ej2-angular-richtexteditor';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { NgxScannerQrcodeModule } from 'ngx-scanner-qrcode';
import { ToastrModule } from 'ngx-toastr';
import { ApplicationPipeModule } from './application-pipes/applicationpipes.module';
import { ForgotComponent } from './authentication/forgot/forgot.component';
import { LockscreenComponent } from './authentication/lockscreen/lockscreen.component';
import { ResetPasswordComponent } from './authentication/reset-password/reset-password.component';
import { SigninModalComponent } from './authentication/signin-modal/signin-modal.component';
import { SigninUumComponent } from './authentication/signin-uum/signin-uum.component';
import { SigninComponent, DialogOverviewExampleDialog, CreatePassword } from './authentication/signin/signin.component';
import { SignupComponent } from './authentication/signup/signup.component';
import { QuickAppointmentsComponent } from './core/admin-layout/dynamic-components/quick-appointments/quick-appointments.component';
import { QuickLinksComponent } from './core/admin-layout/dynamic-components/quick-links/quick-links.component';
import { BarcodeScannerComponent } from './core/barcode-scanner/barcode-scanner.component';
import { CancelDialogComponent } from './core/cancel-dialog/cancel-dialog.component';
import { ClinicareViewDialogComponent } from './core/clinicare-view-dialog/clinicare-view-dialog.component';
import { ComposeMailDialogComponent } from './core/compose-mail-dialog/compose-mail-dialog.component';
import { ConfirmDialogComponent } from './core/confirm-dialog/confirm-dialog.component';
import { ConfirmDynamicDialogComponent } from './core/confirm-dynamic-dialog/confirm-dynamic-dialog.component';
import { ExportFileDialogComponent } from './core/export-file-dialog/export-file-dialog.component';
import { ExtendDialogComponent } from './core/extend-dialog/extend-dialog.component';
import { InfoDialogComponent } from './core/info-dialog/info-dialog.component';
import { InfoDynamicDialogComponent } from './core/info-dynamic-dialog/info-dynamic-dialog.component';
import { MultiLanguageService } from './core/multi-language/multi-language.service';
import { NotFoundComponent } from './core/not-found/not-found.component';
import { PatientNotesDialogComponent } from './core/patient-notes-dialog/patient-notes-dialog.component';
import { PatientSearchModule } from './core/patient-search/patient-search.module';
import { PaymentDialogComponent } from './core/payment-dialog/payment-dialog.component';
import { PdfViewerComponent } from './core/pdf-viewer/pdf-viewer.component';
import { SortByPipe } from './core/pipes/sortByPipe.pipe';
import { ProfileComponent, PasswordDialogComponent } from './core/profile/profile.component';
import { ConfirmRemarkComponent } from './core/remark-dialog/remark-dialog.component';
import { TextEditorTemplateComponent } from './core/text-editor-template/text-editor-template/text-editor-template.component';
import { BarChartComponent } from './dashboard-components/bar-chart/bar-chart.component';
import { CounterComponent } from './dashboard-components/counter/counter.component';
import { DonutComponent } from './dashboard-components/donut/donut.component';
import { NegativeStackComponent } from './dashboard-components/negative-stack/negative-stack.component';
import { PieChartComponent } from './dashboard-components/pie-chart/pie-chart.component';
import { PieRadiusComponent } from './dashboard-components/pie-radius/pie-radius.component';
import { ProgressBarComponent } from './dashboard-components/progress-bar/progress-bar.component';
import { RangeColumnComponent } from './dashboard-components/range-column/range-column.component';
import { TableComponent } from './dashboard-components/table/table.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { OnlyAmountDirective } from './directives/amount.directive';
import { DoubleClickDirective } from './directives/double-click.directive';
import { OnlyNumberDirective } from './directives/number.directive';
import { MainDashboardComponent } from './main-dashboard/main-dashboard.component';
import { MaterialModule } from './modules/material.module';
import { PushNotificationModule } from './modules/push-notification/push-notification.module';
import { SharedModule } from './modules/shared/shared.module';
import { ApplicationConfigService } from './services/application-config.service';
import { AuthService } from './services/auth.service';
import { CopDecimalCalculateService } from './services/cop-decimal-calculate.service';
import { DataService } from './services/data.service';
import { ElasticService } from './services/elastic.service';
import { EnvServiceProvider } from './services/env.service.provider';
import { FileUploadService } from './services/file-upload.service';
import { MasterService } from './services/masters.service';
import { PatientService } from './services/patient.service';
import { qmsSocketService } from './services/qms.socket.service';
import { QuickLinkService } from './services/quick-link.service';
import { ReportService } from './services/report.service';
import { SearchService } from './services/search.service';
import { SocketService } from './services/socket.service';
import { UtilsService } from './services/utils.service';
import { AuthGuardGuard } from './shared/authguard/auth-guard.guard';
import { UiConfigService } from './ui-configuration/ui-config.service';
import { AppBaseService } from './services/http.service';
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { NgxEchartsModule } from "ngx-echarts";
import { NgxDaterangepickerMd } from "ngx-daterangepicker-material";
import {
  ChartAllModule,
  AccumulationChartAllModule,
  RangeNavigatorAllModule,
} from "@syncfusion/ej2-angular-charts";
import { DashboardLayoutModule } from '@syncfusion/ej2-angular-layouts';
import { AppComponent } from './core/app-base/app.component';
import { GeneralUrlService } from './regservices/SgeneralUrl.service';
import { MasterUrlService } from './regservices/SmasterUrl.service';
import { AppointmentService } from './regservices/Sappointment.service';
import { patientGetDataservice } from './regservices/SpatientGetData.service';
import { SearchHeaderService } from './regservices/SsearchHeader.service';
import { PatientAppointmentDialogComponent } from './patient-registration/patient-appointment-dialog/patient-appointment-dialog.component';
import { UnsavedFormDilogComponent } from './patient-registration/unsaved-form-dilog/unsaved-form-dilog.component';
import { ConfirmRemarksComponent } from './update-sponsor/confirm-remarks/confirm-remarks.component';
import { GlAttachmentComponent } from './update-sponsor/gl-attachment/gl-attachment.component';
import { AddSponsorDialogComponent } from './update-sponsor/update-sponsor/add-sponsor.dialog';
import { ViewDocumentComponent } from './regshared/view-document/view-document.component';
import { PdfViewer2Component } from './core/pdf-viewer2/pdf-viewer2.component';
import { PdfViewer3Component } from './core/pdf-viewer3/pdf-viewer3.component';
import { PatientRegistrationComponent } from './patient-registration/patient-registration.component';
import { CameraDialogComponent } from './regshared/camera-dialog/camera-dialog.component';
import { RegsuccessDialogComponent } from './regshared/regsuccess-dialog/regsuccess-dialog.component';
import { UploadfileComponent } from './regshared/uploadfile/uploadfile.component';
import { UpdateSponsorComponent } from './update-sponsor/update-sponsor/update-sponsor.component';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { NgxUiLoaderModule } from 'ngx-ui-loader';
import { DiagnosticDetailsComponent } from './core/diagnostic-details/diagnostic-details.component';
import { CdkDetailRowDirective } from './lab-clinical-orders/cdk-detail-row.directive';
import { LabClinicalOrdersComponent } from './lab-clinical-orders/lab-clinical-orders.component';
import { EncounterAppointmnetComponent } from './regshared/encounter-appointmnet/encounter-appointmnet.component';
import { SidebarModule } from 'ngx-angular-sidebar';
import { DateAdapter, MAT_DATE_LOCALE } from "@angular/material/core";
import { CustomDateAdapter } from "./services/custom-date-adapter";
import { DateRange } from "@angular/material/datepicker";
import { CustomDateRangeAdapter } from "./services/custom-daterange-adapter";
import { StoreDialogComponent } from './regshared/store-dialog/store-dialog.component';
import { WebcamModule } from 'ngx-webcam';
import { NewSigninComponent } from './authentication/new-signin/new-signin.component';
import { LabSigninComponent } from './authentication/lab-signin/lab-signin.component';
import { DuplicatePatientDialogComponent } from './regshared/duplicate-patient-dialog/duplicate-patient-dialog.component';
import { PatientUpdateComponent } from './core/patient-update/patient-update.component';
import { IdleTimeoutService } from './services/idle-timeout.service';
import { CashCounterDialogComponent } from './regshared/cash-counter-dialog/cash-counter-dialog.component';
import { PdfViewer4Component } from './core/pdf-viewer4/pdf-viewer4.component';
import { AuthCallbackComponent } from './authentication/auth-callback/auth-callback.component';
import { ConfirmPrintDialogComponent } from './core/confirm-print-dialog/confirm-print-dialog.component';
import { CriticalHeilthInfoComponent } from './regshared/critical-heilth-info/critical-heilth-info.component';
import { DateTimePickerModule } from '@syncfusion/ej2-angular-calendars';
import { AddotpComponent } from './authentication/addotp/addotp.component';
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

@NgModule({
  declarations: [
    AppComponent,
    MedcareCoreUiComponent,
    MenuComponent,
    HeaderComponent,
    SidebarComponent,
    AdminLayoutComponent,
    AuthLayoutComponent,
    AccordionAnchorDirective,
    AccordionLinkDirective,
    AccordionDirective,
    settingsDialog,
    profileDialog,
    unitDialog,
    ConfirmDialogComponent,
    ConfirmDynamicDialogComponent,
    ConfirmRemarkComponent,
    CancelDialogComponent,
    ExtendDialogComponent,
    SigninComponent,
    SigninUumComponent,
    LockscreenComponent,
    SignupComponent,
    ForgotComponent,
    DashboardComponent,
    InfoDialogComponent,
    DialogOverviewExampleDialog,
    CreatePassword,
    NotFoundComponent,
    PaymentDialogComponent,
    InfoDynamicDialogComponent,
    OnlyNumberDirective,
    OnlyAmountDirective,
    ProfileComponent,
    PasswordDialogComponent,
    QuickLinksComponent,
    QuickAppointmentsComponent,
    SortByPipe,
    DoubleClickDirective,
    PdfViewerComponent,
    ExportFileDialogComponent,
    BarChartComponent,
    MainDashboardComponent,
    PieChartComponent,
    NegativeStackComponent,
    PieRadiusComponent,
    RangeColumnComponent,
    DonutComponent,
    ProgressBarComponent,
    SigninModalComponent,
    TableComponent,
    CounterComponent,
    BarcodeScannerComponent,
    ComposeMailDialogComponent,
    TextEditorTemplateComponent,
    ResetPasswordComponent,
    PatientRegistrationComponent,
    PatientNotesDialogComponent,
    CameraDialogComponent,
    RegsuccessDialogComponent,
    UploadfileComponent,
    PatientAppointmentDialogComponent,
    UnsavedFormDilogComponent,
    UpdateSponsorComponent,
    AddSponsorDialogComponent,
    GlAttachmentComponent,
    ConfirmRemarksComponent,
    PdfViewer2Component,
    PdfViewer3Component,
    ViewDocumentComponent,
    ClinicareViewDialogComponent,
    LabClinicalOrdersComponent,
    CdkDetailRowDirective,
    DiagnosticDetailsComponent,
    EncounterAppointmnetComponent,
    StoreDialogComponent,
    NewSigninComponent,
	LabSigninComponent,
 DuplicatePatientDialogComponent,
 PatientUpdateComponent,
 CashCounterDialogComponent,
 PdfViewer4Component,
 AuthCallbackComponent,
 ConfirmPrintDialogComponent,
 CriticalHeilthInfoComponent,
 AddotpComponent
  ],
  imports: [
    DateTimePickerModule,
    RouterModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    HttpClientModule,
    ReactiveFormsModule,
    MaterialModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    NgbModule,
    SidebarModule.forRoot(),
    NgxEchartsModule,
    CommonModule,
    ApplicationPipeModule,
    PatientSearchModule,
    ToastrModule.forRoot({
      timeOut: 5000,
      preventDuplicates: true,
      closeButton: true,
      disableTimeOut: false,
      progressBar: true,
      progressAnimation: "increasing",
    }),
    PushNotificationModule,
    NgxDaterangepickerMd.forRoot(),
    ChartAllModule,
    AccumulationChartAllModule,
    RangeNavigatorAllModule,
    DashboardLayoutModule,
    NgxScannerQrcodeModule,
    RichTextEditorModule,
    SharedModule,
    NgxMatSelectSearchModule,
    NgxUiLoaderModule,
    LoadingBarModule,
    WebcamModule
  ],
  providers: [
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    AuthGuardGuard,
    SearchService,
    AuthService,
    DataService,
    ElasticService,
    UiConfigService,
    PatientService,
    MasterService,
    UtilsService,
    EnvServiceProvider,
    ApplicationConfigService,
    SocketService,
    QuickLinkService,
    MultiLanguageService,
    ReportService,
    FileUploadService,
    qmsSocketService,
    CopDecimalCalculateService,
    GeneralUrlService,
    MasterUrlService,
    patientGetDataservice,
    SearchHeaderService,
    AppointmentService,
    IdleTimeoutService,
    { provide: MAT_DATE_LOCALE, useValue:  localStorage.getItem('lan') === 'th' ? 'th-TH' : 'en-US' }, // Thai Locale    { provide: DateAdapter, useClass: CustomDateAdapter }, // Custom Adapter
    { provide: DateAdapter, useClass: CustomDateAdapter }, // Custom Adapter
    //{ provide: DateRange, useClass: CustomDateRangeAdapter }, // Custom daterange Adapter
  
  ],
  exports:[LabSigninComponent],
  bootstrap: [MedcareCoreUiComponent],
  // entryComponents: [
  //   settingsDialog,
  //   profileDialog,
  //   unitDialog,
  //   ConfirmDialogComponent,
  //   ConfirmDynamicDialogComponent,
  //   ConfirmRemarkComponent,
  //   CancelDialogComponent,
  //   ExtendDialogComponent,
  //   InfoDialogComponent,
  //   DialogOverviewExampleDialog,
  //   CreatePassword,
  //   PaymentDialogComponent,
  //   InfoDynamicDialogComponent,
  //   PasswordDialogComponent,
  //   QuickLinksComponent,
  //   QuickAppointmentsComponent,
  //   BarcodeScannerComponent,
  //   PatientNotesDialogComponent,
  //   PatientAppointmentDialogComponent,
  //   UnsavedFormDilogComponent,
  //   AddSponsorDialogComponent,
  //   GlAttachmentComponent,
  //   ConfirmRemarksComponent,
  //   ClinicareViewDialogComponent
  // ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MedcareCoreUiModule { 
  public static forRoot(environment: any): ModuleWithProviders<any> {
    return {
      ngModule: MedcareCoreUiModule,
      providers: [
        AppBaseService,
        {
          provide: "env", // you can also use InjectionToken
          useValue: environment,
        },
      ],
    };
  }
}
